/**
 * The note that stands where a cleared chat attachment used to be (0353).
 *
 * ── Why a `text` part and not an emptied `file` part ──────────────────
 *
 * The obvious shape is to keep the `file` part and empty its bytes. It is
 * also the one that breaks, in two ways the tests could not have found
 * because both live outside this feature:
 *
 *  1. `convert-messages.ts` drops a content-less TEXT attachment entirely
 *     — its "say something about this" branch fires only when there is an
 *     error, a url or a handle, and an emptied part has none of the three.
 *     The model would then be answering about a conversation in which the
 *     file had never existed: asked "how many names were on that list",
 *     it has no way to know the list was ever there, so it answers from
 *     whatever else is in the thread instead of saying the file is gone.
 *  2. An emptied part that still carries its `handle` reaches the branch
 *     that sends `url: part.url` as-is — an EMPTY url — and the provider
 *     rejects the whole request. The member presses send and the turn
 *     dies with a disabled composer.
 *
 * A `text` part has neither problem: every consumer already handles one
 * (the same reason #817's oversized stand-in is a `text` part), the model
 * reads the sentence, and nothing anywhere has to learn a new shape.
 *
 * ── Two readers, one part ─────────────────────────────────────────────
 *
 * The MODEL reads `text`, in English, like every other attachment
 * breadcrumb in `convert-messages.ts`. A PERSON reads the chip the chat
 * draws from {@link ClearedAttachment} in `metadata`, in their own
 * language — i18n is browser-only here, and a sentence frozen into a
 * database row at deletion time could not be translated later anyway.
 *
 * `metadata` is a freeform bag already declared on `TextPart`
 * (`message.ts`), so zod keeps it and no schema anywhere changes.
 */

/** The marker's key inside a text part's `metadata`. A namespaced name:
 *  `metadata` is shared with everything else that annotates a text part. */
export const CLEARED_ATTACHMENT_KEY = 'papanClearedAttachment'

/** What was cleared, for the chip the chat draws in its place. */
export interface ClearedAttachment {
  /** The file's name as it was attached. The one thing a member needs to
   *  recognise what went. */
  filename: string
  /** The calendar day it was cleared, `YYYY-MM-DD`. A DATE, not a
   *  timestamp: the chip says "cleared on the 30th", and storing an
   *  instant would invite rendering it in the wrong timezone. */
  clearedAt: string
  /** What it took, so the chip can say how much came back. */
  sizeBytes: number
  /**
   * WHO cleared it, because the chip says different things.
   *
   *   `member-file-age`  their own standing rule did it, months after
   *                      they switched it on and probably without them
   *                      thinking about it again.
   *   `member-manual`    they pressed Delete on it themselves.
   *
   * The second one earns a note too: the member may not remember doing
   * it (owner, 2026-10-30). Before this, a manual delete took the chip
   * away and left a conversation reading as if the file had never been
   * attached.
   */
  rule: ClearedAttachmentRule
}

export type ClearedAttachmentRule = 'member-file-age' | 'member-manual'

const RULES: readonly ClearedAttachmentRule[] = ['member-file-age', 'member-manual']

/** The sentence the MODEL sees. English, like every other breadcrumb in
 *  `convert-messages.ts` — this one is stored rather than injected, but it
 *  is read by the same reader for the same reason.
 *
 *  It says plainly that the file is gone AND that the model must not
 *  answer as if it had read it: a filename alone invites a confident
 *  answer about contents nobody has. */
export function clearedAttachmentText(
  filename: string, clearedAt: string, rule: ClearedAttachmentRule,
): string {
  const how = rule === 'member-manual'
    ? 'was deleted by the person who attached it'
    : "was automatically cleared by that person's own chat-storage housekeeping"
  return `[Attached file "${filename}" ${how} on ${clearedAt}, `
    + `and its content is no longer available. Do not answer as if you had read it.]`
}

/** `YYYY-MM-DD` for an instant, in UTC — the day the file-age rule
 *  stamps. A manual delete stamps the member's own day instead
 *  (`clearedAttachmentDayFrom`, D-d); readers take the stored day as is. */
export function clearedAttachmentDay(at: Date): string {
  return at.toISOString().slice(0, 10)
}

const HOUR_MS = 60 * 60 * 1000

/**
 * The day a MANUAL delete is stamped with: the member's own calendar day,
 * as their browser sends it, when it is believable — else the UTC day.
 *
 * In UTC, a member in Malaysia deleting a file between midnight and 8am
 * read "deleted on" the day before (decision D-d, 2026-10-31). The rule's
 * own clearing stays on UTC: nobody is watching the clock when it runs.
 *
 * Believable = a real `YYYY-MM-DD` that is the date SOMEWHERE right now:
 * between the day at UTC−12 and the day at UTC+14, the two ends of the
 * world's timezones. Anything else is a wrong clock or a hand-made
 * request and would put a date in the conversation nobody could explain.
 * Missing (an old tab) falls back the same way.
 */
export function clearedAttachmentDayFrom(clientDay: unknown, now: Date): string {
  const utc = clearedAttachmentDay(now)
  if (typeof clientDay !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(clientDay)) return utc
  const at = Date.parse(`${clientDay}T00:00:00Z`)
  // `Date.parse` rolls 2026-02-30 over to March: a round trip that does
  // not come back unchanged was not a real day.
  if (!Number.isFinite(at) || new Date(at).toISOString().slice(0, 10) !== clientDay) return utc
  // Same-length `YYYY-MM-DD` strings compare as dates.
  const earliest = clearedAttachmentDay(new Date(now.getTime() - 12 * HOUR_MS))
  const latest = clearedAttachmentDay(new Date(now.getTime() + 14 * HOUR_MS))
  return clientDay >= earliest && clientDay <= latest ? clientDay : utc
}

/**
 * Read the marker off a part's metadata — the ONE reader, so the chat and
 * the tests cannot come to disagree about what a cleared part looks like.
 *
 * Returns null for anything that is not one, INCLUDING a part whose
 * metadata carries the key with the wrong shape: this is untrusted stored
 * JSON from however many releases ago, and a chip built from a missing
 * filename would render as an empty grey box the member cannot explain.
 */
export function readClearedAttachment(metadata: unknown): ClearedAttachment | null {
  if (!metadata || typeof metadata !== 'object') return null
  const raw = (metadata as Record<string, unknown>)[CLEARED_ATTACHMENT_KEY]
  if (!raw || typeof raw !== 'object') return null
  const m = raw as Record<string, unknown>
  if (typeof m.filename !== 'string' || !m.filename) return null
  if (typeof m.clearedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(m.clearedAt)) return null
  return {
    filename: m.filename,
    clearedAt: m.clearedAt,
    sizeBytes: typeof m.sizeBytes === 'number' && m.sizeBytes >= 0 ? m.sizeBytes : 0,
    // An unrecognised value must not cost the member their chip — the
    // filename and the date are what the chip is made of — so it falls
    // back to the rule that wrote the first of these rather than
    // refusing the whole marker.
    rule: RULES.includes(m.rule as ClearedAttachmentRule)
      ? (m.rule as ClearedAttachmentRule)
      : 'member-file-age',
  }
}

/** The whole `metadata` bag for a cleared part. Built here so the writer
 *  cannot spell the key differently from {@link readClearedAttachment}. */
export function clearedAttachmentMetadata(info: ClearedAttachment): Record<string, unknown> {
  return { [CLEARED_ATTACHMENT_KEY]: info }
}
